import React from "react";

import { faArrowLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { ContactSupportLink } from "~/ui/legacyFellowIndex";

import { AuthBase } from "../AuthBase";
import { Button, Input, SignupHint } from "../components/";
import { ReactComponent as EmailSent } from "../images/email-sent.svg";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";

import styles from "../css/Auth.module.css";

interface FindByEmailState {
	emailInput: string;
	emailSent: boolean;
	loading: boolean;
	errorMessage: string | undefined;
}

type FindByEmailResult =
	| {
			success: true;
	  }
	| {
			success: false;
			message: string | undefined;
	  };

export default class FindByEmail extends AuthBase<AuthProps, FindByEmailState> {
	state: FindByEmailState = {
		emailInput: "",
		emailSent: false,
		loading: false,
		errorMessage: undefined,
	};

	documentTitle = "Find | Login";

	getPaneComponent() {
		return WhitelabelPane;
	}

	getPaneProps() {
		return { bgStyle: "purple" as const };
	}

	findWorkspace = async () => {
		const { emailInput } = this.state;
		if (!emailInput) return;

		this.setState({ loading: true });

		const token = await recaptcha("find_by_email");
		const analyticsData = { feature: "Authentication" };

		const data = new FormData();
		data.append("email", emailInput);
		data.append("captcha_token", token);

		if (window.location.search.includes("redirectBackToDesktopOnLastOauthStep=1")) {
			data.append("redirectBackToDesktopOnLastOauthStep", "1");
		}

		const response = await fetch("/auth/search/email/", {
			method: "POST",
			body: data,
			headers: {
				"x-client-session-id": window.CLIENT_SESSION_ID,
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		}).catch(({ message }) => {
			dispatchAnalyticsTrack("Find By Email Step Failed", analyticsData);
			this.setState({ loading: false, errorMessage: message });
		});

		if (!response) return;
		const result: FindByEmailResult = await response.json();

		if (result.success) {
			dispatchAnalyticsTrack("Find By Email Step Completed", analyticsData);
			this.setState({ loading: false, emailSent: true });
		} else {
			dispatchAnalyticsTrack("Find By Email Step Failed", analyticsData);
			this.setState({ loading: false, errorMessage: result.message });
		}
	};

	onSearchValueChange = (e: React.SyntheticEvent<HTMLInputElement, Event>) => {
		this.setState({ emailInput: e.currentTarget.value });
	};

	onSearchKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.findWorkspace();
		}
	};

	clearSearchValue = () => this.setState({ emailInput: "" });

	renderEmailSent() {
		return (
			<div>
				<EmailSent />
				<h2>Check your inbox</h2>
				<h5 className={styles.emailSent}>
					We sent you an email with links to all the Fellow workspaces you have access to. If you experience
					difficulties,{" "}
					<ContactSupportLink message="Hi! I can't seem to find my workspace.">contact us</ContactSupportLink>.
				</h5>

				{this.props.history.length > 1 && (
					<div className={styles.backButton} onClick={this.onBackClick}>
						<FontAwesomeIcon icon={faArrowLeft} />
						<span>Back</span>
					</div>
				)}
			</div>
		);
	}

	renderStep() {
		const { emailInput, loading, errorMessage, emailSent } = this.state;

		if (emailSent) return this.renderEmailSent();

		return (
			<div className={styles.authFlex}>
				<div className={styles.authFind}>
					<h2>Find your workspace URL</h2>
					<h5>Enter your email and we&apos;ll send you links to all the Fellow workspaces you have access to.</h5>

					<div className={styles.authFindBody}>
						<Input
							value={emailInput}
							error={!!errorMessage}
							placeholder="Work email"
							onChange={this.onSearchValueChange}
							onKeyDown={this.onSearchKeydown}
							onClear={this.clearSearchValue}
						/>
					</div>
				</div>

				<div className={styles.authFormNav}>
					<Button
						primary
						onClick={this.findWorkspace}
						disabled={!emailInput || loading}
						className={styles.authFindButton}
					>
						Email me my Fellow links
					</Button>
				</div>

				{this.props.history.length > 1 && (
					<div className={styles.backButton} onClick={this.onBackClick}>
						<FontAwesomeIcon icon={faArrowLeft} />
						<span>Back</span>
					</div>
				)}
			</div>
		);
	}

	renderFooter() {
		return <SignupHint />;
	}
}
